/**
 * Full-screen and in-page states the shell owns: boot splash, signed out,
 * no office access, boot failure, not found, and "pick an office".
 */
import { Link } from '@tanstack/react-router';
import { PRODUCT } from '@velora/contracts/product';
import type { ShellOffice } from '@velora/contracts/shell';
import { BrandMark, Button, EmptyState, officeColor, Spinner } from '@velora/ui';
import { Building2, CircleAlert, Compass, KeyRound, Layers, Lock } from 'lucide-react';
import type { CSSProperties } from 'react';

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="shell-center" role="main">
      <div className="shell-center__brand">
        <BrandMark name={PRODUCT.name} size={36} wordmark />
      </div>
      {children}
    </div>
  );
}

export function BootSplash() {
  return (
    <Centered>
      <div className="shell-center__loading" role="status">
        <Spinner size={18} label={`Loading ${PRODUCT.shortName}`} />
        <span>Loading your offices</span>
      </div>
    </Centered>
  );
}

export function SignedOut({ loginPath }: { loginPath: string | undefined }) {
  return (
    <Centered>
      <EmptyState
        icon={<KeyRound size={22} />}
        title="Sign in to continue"
        description={`Your ${PRODUCT.shortName} session has ended or this device has not signed in yet.`}
        actions={
          loginPath ? (
            <Button variant="primary" onClick={() => window.location.assign(loginPath)}>
              Sign in
            </Button>
          ) : (
            <p className="shell-muted">Ask your practice administrator for a sign-in link.</p>
          )
        }
      />
    </Centered>
  );
}

export function NoOffices({ userName }: { userName: string }) {
  return (
    <Centered>
      <EmptyState
        icon={<Building2 size={22} />}
        title="No offices yet"
        description={`${userName}, your account is not linked to any office. An owner or office manager can give you access in Settings, Users.`}
      />
    </Centered>
  );
}

export function BootError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Centered>
      <EmptyState
        icon={<CircleAlert size={22} />}
        title={`${PRODUCT.shortName} could not start`}
        description={message}
        actions={
          <Button variant="primary" onClick={onRetry}>
            Try again
          </Button>
        }
      />
    </Centered>
  );
}

export function NotFoundPage() {
  return (
    <div className="shell-page">
      <EmptyState
        icon={<Compass size={22} />}
        title="Page not found"
        description="That page does not exist or has moved."
        actions={
          <Button asChild>
            <Link to="/">Go to Home</Link>
          </Button>
        }
      />
    </div>
  );
}

/**
 * Shown when the signed-in role lacks the route's permission in the offices
 * in scope (a typed URL or an old link; the nav already hides the item).
 */
export function NoAccess({ title, otherOffice }: { title: string; otherOffice: boolean }) {
  return (
    <div className="shell-page" data-testid="no-access">
      <EmptyState
        icon={<Lock size={22} />}
        title={`${title} is not part of your role`}
        description={
          otherOffice
            ? `Your role does not include ${title} for the office selected. Switch to an office where you have it, or ask an owner or office manager.`
            : `Your role does not include ${title}. An owner or office manager can change your role in Settings, Users.`
        }
        actions={
          <Button asChild>
            <Link to="/">Go to Home</Link>
          </Button>
        }
      />
    </div>
  );
}

/** Shown when a one-office page (schedule, charting) is opened in "All offices". */
export function OfficeRequired({
  title,
  offices,
  onPick,
}: {
  title: string;
  offices: readonly ShellOffice[];
  onPick: (locationId: string) => void;
}) {
  return (
    <div className="shell-page">
      <EmptyState
        icon={<Layers size={22} />}
        title={`Pick an office for ${title}`}
        description={`${title} works on one office at a time. Choose which office to open; you can switch at any time with the office selector or the L key.`}
        actions={
          <div className="shell-office-pick" role="group" aria-label="Offices">
            {offices.map((o) => (
              <Button
                key={o.id}
                onClick={() => onPick(o.id)}
                leadingIcon={
                  <span
                    className="shell-dot"
                    style={{ '--dot': officeColor(o, o.colorIndex) } as CSSProperties}
                    aria-hidden
                  />
                }
              >
                {o.name}
              </Button>
            ))}
          </div>
        }
      />
    </div>
  );
}
